import React, { useEffect, useRef, useState } from 'react';
import { gsap } from 'gsap';
import { ScrollTrigger } from 'gsap/ScrollTrigger';
import './contact.css';
import heroImg from '../assets/heroimg.png';

gsap.registerPlugin(ScrollTrigger);

const Contact = () => {
  const pageRef = useRef(null);
  const [form, setForm] = useState({ name: '', email: '', phone: '', subject: '', message: '' });
  const [errors, setErrors] = useState({});
  const [sent, setSent] = useState(false);

  useEffect(() => {
    const page = pageRef.current;
    if (!page) return undefined;

    const ctx = gsap.context(() => {
      gsap.from('.contact-hero-item', {
        y: 46,
        opacity: 0,
        filter: 'blur(8px)',
        duration: 0.9,
        ease: 'power3.out',
        stagger: 0.12,
      });

      gsap.utils.toArray('[data-contact-reveal]').forEach((item) => {
        gsap.fromTo(
          item,
          { y: 52, filter: 'blur(8px)' },
          {
            y: 0,
            filter: 'blur(0px)',
            duration: 0.82,
            ease: 'power3.out',
            immediateRender: false,
            scrollTrigger: { trigger: item, start: 'top 86%', once: true },
          }
        );
      });
    }, page);

    return () => ctx.revert();
  }, []);

  const update = (event) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
    setErrors((current) => ({ ...current, [name]: '' }));
  };

  const submit = (event) => {
    event.preventDefault();
    const nextErrors = {};
    if (!form.name.trim()) nextErrors.name = 'Full name is required.';
    if (!form.email.includes('@')) nextErrors.email = 'Please enter a valid email address.';
    if (!form.subject.trim()) nextErrors.subject = 'Please choose a subject.';
    if (form.message.trim().length < 10) nextErrors.message = 'Message must be at least 10 characters.';
    setErrors(nextErrors);
    setSent(Object.keys(nextErrors).length === 0);
  };

  return (
    <main className="contact-page" ref={pageRef}>
      <section className="contact-hero">
        <img src={heroImg} alt="Textile exhibition venue" />
        <div className="contact-hero-overlay" />
        <div className="contact-hero-content">
          <span className="contact-badge contact-hero-item">We are here to help</span>
          <h1 className="contact-hero-item">Get in Touch</h1>
          <p className="contact-hero-item">
            Have questions about registration, booth booking, venue access, or visitor support?
            Send us a message and our team will follow up.
          </p>
        </div>
      </section>

      <section className="contact-layout" data-contact-reveal>
        <form className="contact-form" onSubmit={submit} noValidate>
          <div className="contact-form-heading">
            <span className="contact-badge">Send Us a Message</span>
            <h2>Tell us how we can help.</h2>
          </div>

          <div className="contact-grid">
            <label htmlFor="contact-name">
              Full Name
              <input id="contact-name" name="name" type="text" value={form.name} onChange={update} aria-describedby="name-error" />
              {errors.name && <span className="contact-error" id="name-error">{errors.name}</span>}
            </label>
            <label htmlFor="contact-email">
              Email Address
              <input id="contact-email" name="email" type="email" value={form.email} onChange={update} aria-describedby="email-error" />
              {errors.email && <span className="contact-error" id="email-error">{errors.email}</span>}
            </label>
            <label htmlFor="contact-phone">
              Phone Number
              <input id="contact-phone" name="phone" type="tel" value={form.phone} onChange={update} />
            </label>
            <label htmlFor="contact-subject">
              Subject
              <select id="contact-subject" name="subject" value={form.subject} onChange={update} aria-describedby="subject-error">
                <option value="">What is this regarding?</option>
                <option>Visitor registration</option>
                <option>Booth booking</option>
                <option>Venue information</option>
                <option>Press inquiry</option>
              </select>
              {errors.subject && <span className="contact-error" id="subject-error">{errors.subject}</span>}
            </label>
          </div>

          <label htmlFor="contact-message">
            Message
            <textarea id="contact-message" name="message" rows="6" value={form.message} onChange={update} aria-describedby="message-error" />
            {errors.message && <span className="contact-error" id="message-error">{errors.message}</span>}
          </label>

          {sent && <p className="contact-success" role="status">Thanks. Your message is ready for our team.</p>}

          <button type="submit" className="contact-submit">Send Message</button>
        </form>

        <aside className="contact-side">
          <article className="contact-info-card">
            <span className="contact-badge">Contact Information</span>
            <a href="mailto:info@egypttextileexpo.com">info@egypttextileexpo.com</a>
            <a href="tel:+20212345678">+20 2 1234 5678</a>
            <p>Cairo International Convention Centre, Nasr City, Cairo, Egypt</p>
            <p>Sunday - Thursday / 9:00 AM - 5:00 PM</p>
          </article>

          <article className="contact-map-card">
            <span>Venue Location</span>
            <strong>Exhibition Centre</strong>
            <p>Cairo International Convention Centre</p>
          </article>
        </aside>
      </section>

      <section className="faq-section" data-contact-reveal>
        <span className="contact-badge">FAQ</span>
        <h2>Frequently Asked Questions</h2>
        <div className="faq-grid">
          <article><h3>How do I register as a visitor?</h3><p>You can register through the tickets page or at the venue registration desk.</p></article>
          <article><h3>What are the exhibition hours?</h3><p>Doors open daily during the April 20-23 exhibition program.</p></article>
          <article><h3>Is parking available?</h3><p>Visitor parking is available at the Cairo International Convention Centre.</p></article>
          <article><h3>Can I book a booth as an exhibitor?</h3><p>Yes. Contact our team to review booth options and availability.</p></article>
        </div>
      </section>
    </main>
  );
};

export default Contact;
